import {Button, Select, TextInput} from '@mantine/core';
import {useFormik} from 'formik';
import {BillingProvidersAutocomplete} from '../../billing-providers/inputs/BillingProvidersAutocomplete';
import {createProviderMapping} from '../services/ProviderMappingsService';
import {useQueryClient} from '@tanstack/react-query';
import {useParams, useLocation} from 'react-router-dom';

interface CreateFormProps {
    onSuccess?: () => void;
}

export const CreateForm = ({ onSuccess }: CreateFormProps) => {
    const queryClient = useQueryClient();
    const params = useParams();
    const location = useLocation();
    
    // Determine context from URL
    const isCustomerContext = location.pathname.includes('/customers/');
    const isProductContext = location.pathname.includes('/products/') && !location.pathname.includes('/prices/');
    const isPriceContext = location.pathname.includes('/prices/');
    
    const initialEntityType = isCustomerContext ? 'Customer' : isPriceContext ? 'Price' : isProductContext ? 'Product' : '';
    const initialEntityId = isPriceContext ? params.priceId || '' : params.id || '';
    
    const formik = useFormik({
        initialValues: {
            entityType: initialEntityType || '',
            localEntityId: initialEntityId || '',
            billingProviderId: ''
        },
        onSubmit: async (values) => {
            try {
                await createProviderMapping({
                    entityType: values.entityType,
                    localEntityId: values.localEntityId,
                    billingProviderId: values.billingProviderId
                });
                
                // Invalidate and refetch provider mappings
                queryClient.invalidateQueries({ queryKey: ['provider-mappings'] });
                queryClient.refetchQueries({ queryKey: ['provider-mappings'] });
                
                onSuccess?.();
            } catch (error) {
                console.error('Failed to create provider mapping:', error);
            }
        }
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <Select
                label="Entity Type"
                placeholder="Select entity type"
                data={[
                    { value: 'Product', label: 'Product' },
                    { value: 'Customer', label: 'Customer' },
                    { value: 'Price', label: 'Price' }
                ]}
                {...formik.getFieldProps('entityType')}
                onChange={(value) => formik.setFieldValue('entityType', value)}
                disabled={!!initialEntityType}
                mb="md"
            />

            <TextInput
                label="Local Entity ID"
                placeholder="Enter local entity ID"
                {...formik.getFieldProps('localEntityId')}
                disabled={!!initialEntityId}
                mb="md"
            />

            <BillingProvidersAutocomplete
                field={formik.getFieldProps('billingProviderId')}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                form={formik as any}
                meta={formik.getFieldMeta('billingProviderId')}
                label="Billing Provider"
                placeholder="Select billing provider"
                mb="md"
            />

            <Button type="submit" mr="sm" loading={formik.isSubmitting}>Create</Button>
            <Button variant="outline" onClick={onSuccess}>Cancel</Button>
        </form>
    );
};